import {Flex, Image, useTheme, Center, Spinner, useMediaQuery} from '@chakra-ui/react';
import React, {useEffect, useRef} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useFetchPublicEvent} from '../../hooks/useEvent';
import FallbackImg from '../../assets/images/fallback.jpg';
import {Event} from '../../types/article';
import DefaultEventImage from '../../assets/images/default_event.png';
import PreviewHeader from './PreviewHeader';
import PreviewAbout from './PreviewAbout';
import useAuth from '../../hooks/useAuth';
import {isMobile} from '../../utils/deviceCheck';
import BackButton from '../../components/BackButton';
import {fetchPublicOffer} from '../../endpoints/api';
import useOfferModal from '../../hooks/useOfferModal';
import EventHelmet from '../Event/EventHelmet';
import {logEventViewItem} from '../../utils/analytics';
import {navigateBackOrHome} from '../../utils/history';

const EventPreview = () => {
  const theme = useTheme();
  const {eventId} = useParams();
  const {user} = useAuth();
  const navigate = useNavigate();
  const fetchEvent = useFetchPublicEvent(Number(eventId));
  const event: Event = fetchEvent.data?.data;
  const viewItemTracked = useRef(false);

  const isLoading = fetchEvent.isLoading;

  const [searchParams] = useSearchParams();
  const {showOfferModal} = useOfferModal();

  useEffect(() => {
    if (event && event.is_meetup) {
      navigate(`/meet-up/${event.id}/preview`, {replace: true});
      return;
    }
    if (searchParams.get('offer')) {
      fetchPublicOffer(searchParams.get('offer'))
        .then((response) => {
          // Check if response and data exist
          if (!response || !response.data) {
            throw new Error('No data available'); // This will be caught in the `catch` block
          }

          const {data} = response;

          if (user && event && isMobile) {
            // Remove the old offer parameter before adding the new one
            const params = new URLSearchParams(searchParams);
            params.delete('offer');
            const currentQueryString = params.toString();

            // Construct the new path with the updated `offer` parameter
            const newPath = currentQueryString
              ? `/event/${event.id}?${currentQueryString}&offer=${data.id}`
              : `/event/${event.id}?offer=${data.id}`;

            navigate(newPath, {replace: true});
          } else {
            showOfferModal({offer: data, isPreview: true});
          }
        })
        .catch(() => {
          if (user && event) {
            const queryString = searchParams.toString();
            const newPath = queryString
              ? `/event/${event.id}?${queryString}`
              : `/event/${event.id}`;

            isMobile ? navigate(newPath, {replace: true}) : null;
          }
        });
    } else if (user && event) {
      const queryString = searchParams.toString();
      const newPath = queryString ? `/event/${event.id}?${queryString}` : `/event/${event.id}`;

      isMobile ? navigate(newPath, {replace: true}) : null;
    } else if (event) {
      if (event.is_meetup) {
        const queryString = searchParams.toString();
        const newPath = queryString
          ? `/meet-up/${event.id}/preview?${queryString}`
          : `/meet-up/${event.id}/preview`;

        navigate(newPath, {replace: true});
        return;
      }
    }
  }, [user, event, searchParams.get('offer')]);

  useEffect(() => {
    if (event && !isLoading && !viewItemTracked.current) {
      logEventViewItem(event, {
        page_type: 'preview'
      });
      viewItemTracked.current = true;
    }
  }, [event, isLoading]);

  if (isLoading || event === undefined || (user && isMobile))
    return (
      <Center minH="100vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor={theme.colors.lightGrey}
          color={theme.colors.lightBlue}
          size="lg"
        />
      </Center>
    );

  return (
    <>
      <EventHelmet event={event} />
      <Flex position="relative" height="250px">
        <BackButton onClick={() => navigateBackOrHome(navigate)} m={'.75rem'} pos="relative" />
        <Image
          fallbackStrategy="onError"
          fallbackSrc={FallbackImg}
          src={event?.image_url || DefaultEventImage}
          alt="cover image"
          position="absolute"
          width="100%"
          height="100%"
          objectFit="cover"
          zIndex="-1"
          borderBottomRadius="14px"
        />
      </Flex>
      <PreviewHeader event={event} />
      <PreviewAbout event={event} />
    </>
  );
};

export default EventPreview;
